import { Disclosure, Transition } from '@headlessui/react';
import { rolesManipulator } from 'auth';
import { FormikProvider, useFormik } from 'formik';
import { useSearchParams } from 'react-router-dom';
import { FiltersGroup } from '@/modules/common/components/FiltersGroup.tsx';
import { useGetReportTypesOptions } from '@/modules/common/components/modals/CreateReportModal.tsx';
import { REGISTER_ID_SEARCH_PARAM_KEY } from '@/modules/common/constants/search-params.ts';
import { ReportFilters as ReportFiltersI } from '@/modules/common/pages/Reports.tsx';
import { preventFormDefault } from '@/modules/common/utils/preventFormDefault.ts';
import { SelectHolder } from '@/modules/issuer-dashboard/components/transactions/SelectHolder.tsx';
import { Button, Select } from 'ui';
import { ReportFormat, ReportType } from 'api';

interface IssuerReportFiltersProps {
  handleFiltersChange: (value: ReportFiltersI & Partial<ReportFiltersI>) => void;
  isLoading: boolean;
  isIssuer: boolean;
}

interface IssuerReportFiltersForm {
  type?: ReportType;
  format?: ReportFormat;
  entity_id?: number;
}

interface ReportSelectOption<T> {
  value: T;
  label: string;
}

export const reportFormatsSelection: ReportSelectOption<ReportFormat>[] = [
  { value: ReportFormat.Pdf, label: 'PDF' },
  { value: ReportFormat.Xlsx, label: 'XLSX' },
];

export const ReportFilters = ({
  isLoading,
  handleFiltersChange,
  isIssuer,
}: IssuerReportFiltersProps) => {
  const [searchParams] = useSearchParams();
  const typesOptions = useGetReportTypesOptions(rolesManipulator.current_user_role);
  const register_id = searchParams.get(REGISTER_ID_SEARCH_PARAM_KEY);
  const formik = useFormik<IssuerReportFiltersForm>({
    initialValues: { type: undefined, format: undefined, entity_id: undefined },
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: values => {
      handleFiltersChange(values);
    },
  });

  const handleReportTypeChange = (type?: ReportType | null) => {
    formik.setFieldValue('type', type);
  };

  const handleReportFormatChange = (format?: ReportFormat | null) => {
    formik.setFieldValue('format', format);
  };

  const handleChangeHolder = (entity_id?: number) => {
    formik.setFieldValue('entity_id', entity_id);
  };

  return (
    <Transition
      enter="transition duration-100 ease-out"
      enterFrom="transform scale-95 opacity-0"
      enterTo="transform scale-100 opacity-100"
      leave="transition duration-75 ease-out"
      leaveFrom="transform scale-100 opacity-100"
      leaveTo="transform scale-95 opacity-0"
    >
      <Disclosure.Panel static>
        <FormikProvider value={formik}>
          <form className="flex space-y-10 relative" onSubmit={preventFormDefault}>
            <div className="w-full flex flex-col items-start">
              <FiltersGroup className="w-full flex items-end gap-4">
                <Select
                  isClearable
                  label="Report type"
                  className="w-full"
                  name="type"
                  placeholder="Any type"
                  value={formik.values.type}
                  options={typesOptions}
                  onChange={handleReportTypeChange}
                />
                <Select
                  isClearable
                  label="Report format"
                  className="w-full"
                  name="format"
                  placeholder="Any format"
                  value={formik.values.format}
                  options={reportFormatsSelection}
                  onChange={handleReportFormatChange}
                />
                {isIssuer && register_id && (
                  <SelectHolder
                    setEntityId={handleChangeHolder}
                    entityId={formik.values.entity_id}
                    registerId={register_id}
                    placeholder="Select holder"
                    label=""
                    labelParentSelect="Holder"
                    isClearable={true}
                    showBorderBottom={true}
                    className="max-w-[250px] min-w-[200px]"
                  />
                )}
                <Button onClick={formik.submitForm} loading={isLoading}>
                  Apply
                </Button>
              </FiltersGroup>
            </div>
          </form>
        </FormikProvider>
      </Disclosure.Panel>
    </Transition>
  );
};
